import UUID from 'uuid-js';
import { computed, set } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import Enum from '@square/dashboard-utilities/utils/enum';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import TokenPair from '@square/dashboard-data/models/squareup/common/token/token-pair';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import SurchargeSnapshot from '@square/dashboard-items-data/models/invoices/cart/surcharge-snapshot';
import { CalculationPhase, SurchargeType } from '@square/dashboard-items-data/models/invoices/enums';
import Surcharge from '@square/dashboard-items-data/models/surcharge';
import Money from '@square/dashboard-money/models/money';
import FeeLineItem from '@square/dashboard-items-data/models/invoices/cart/fee-line-item';
const {
  SurchargeCalculationType
} = Surcharge;
const BackingType = new Enum(['ITEMS_SERVICE_SURCHARGE', 'CUSTOM_SURCHARGE']);

// squareup.bills.SurchargeLineItem.Amounts
const Amounts = ProtoBaseModel.extend({
  appliedMoney: attr(MoneyProto)
});

// squareup.bills.SurchargeLineItem.BackingDetails
const BackingDetails = ProtoBaseModel.extend({
  backingType: attr(BackingType),
  surcharge: attr(SurchargeSnapshot)
});

// squareup.bills.SurchargeLineItem.PricingEngineState
export const PricingEngineState = ProtoBaseModel.extend({
  blocklistedTaxIds: repeated(String, {
    defaultValue: []
  })
});

// squareup.bills.SurchargeLineItem
const SurchargeLineItem = ProtoBaseModel.extend({
  amounts: attr(Amounts),
  backingDetails: attr(BackingDetails),
  fees: repeated(FeeLineItem, {
    key: 'fee',
    defaultValue: []
  }),
  pricingEngineState: attr(PricingEngineState),
  surchargeLineItemIdPair: attr(TokenPair),
  writeOnlyDeleted: attr(Boolean),
  amountMoney: computed('_amountMoney', 'backingDetails.surcharge.amount.{cents,currencyCode}', {
    get() {
      if (this._amountMoney !== undefined) {
        return this._amountMoney;
      }
      return this.backingDetails?.surcharge?.amount && Money.fromCents(this.backingDetails.surcharge.amount.cents);
    },
    set(_, value) {
      return set(this, '_amountMoney', value);
    }
  }),
  calculationType: readOnly('backingDetails.surcharge.calculationType'),
  name: readOnly('backingDetails.surcharge.name'),
  percentage: readOnly('backingDetails.surcharge.percentage'),
  clone() {
    const surchargeLineItemClone = SurchargeLineItem.deserialize(SurchargeLineItem.serialize(this));
    surchargeLineItemClone.set('surchargeLineItemIdPair', TokenPair.create({
      clientToken: UUID.create().toString()
    }));
    return surchargeLineItemClone;
  }
}).reopenClass({
  Amounts,
  BackingDetails,
  BackingType,
  createPlaceholder() {
    return this.create({
      surchargeLineItemIdPair: TokenPair.create({
        clientToken: UUID.create().toString()
      }),
      backingDetails: BackingDetails.create({
        backingType: BackingType.ITEMS_SERVICE_SURCHARGE,
        surcharge: SurchargeSnapshot.create({
          calculationPhase: CalculationPhase.SURCHARGE_PHASE,
          calculationType: SurchargeCalculationType.FIXED,
          percentage: 0,
          taxable: false,
          type: SurchargeType.CUSTOM
        })
      })
    });
  }
});
export default SurchargeLineItem;